import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SelectField from '../Input/SelectField';
import HorizontalCheckboxGroup from '../Input/HorizontalCheckboxGroup';
import Tooltip from '../Tooltip';
import { generatorApi } from '../../services/generatorService';
import { FaFilter } from 'react-icons/fa';

const COOKIE_NAME = 'advancedFilterStatuses';

// Cookie utility functions
const setCookie = (name, value, days = 365) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  const cookieValue = `${name}=${JSON.stringify(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  document.cookie = cookieValue;

};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      const rawValue = c.substring(nameEQ.length, c.length);
      try {
        const parsed = JSON.parse(rawValue);

        return parsed;
      } catch (e) {
        console.warn(`[Cookie] Errore parsing cookie ${name}:`, e);
        return null;
      }
    }
  }

  return null;
};

function AdvancedFilterControls({
  filters = {},
  onFilterChange,
  onAdvancedFilterChange,
  theme,
  showStatusFilter = true,
  moduleFilterField = 'MAC_Id_Moduli',
  statusFilterField = 'MAC_Stato',
  onSearch,
}) {
  const themeColor = theme?.themeColor || 'emerald';
  const currentThemeName = theme?.themeName || 'light';
  const isDark = currentThemeName === 'dark';

  // Stati base
  const [products, setProducts] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [selectedProductValue, setSelectedProductValue] = useState(null);
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasExecutedInitialSearch, setHasExecutedInitialSearch] = useState(false);

  // Stati per loading
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingAllModules, setLoadingAllModules] = useState(false);
  const [loadingProductModules, setLoadingProductModules] = useState(false);

  // **CORREZIONE CRITICA**: Inizializzazione corretta dello stato con priorità ai cookie
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const defaultStatuses = ['I', 'C', 'F', 'P', 'M'];
    
    // Se showStatusFilter è false, usa solo i default
    if (!showStatusFilter) {
      return defaultStatuses;
    }
    
    // Controlla prima i cookie se disponibili
    const cookieStatuses = getCookie(COOKIE_NAME);
    if (cookieStatuses && Array.isArray(cookieStatuses) && cookieStatuses.length > 0) {

      return cookieStatuses;
    }
    
    // Poi controlla i filtri esterni (solo se esplicitamente impostati)
    const externalFilters = filters[statusFilterField];
    if (externalFilters && Array.isArray(externalFilters) && externalFilters.length > 0) {

      return externalFilters;
    }
    
    // Default finale

    return defaultStatuses;
  });

  // **CORREZIONE**: Sincronizzazione con filtri esterni solo se sono esplicitamente diversi dai cookie
  useEffect(() => {
    // Non fare nulla se non è inizializzato
    if (!isInitialized) return;
    
    const externalFilters = filters[statusFilterField];
    
    // Controlla se i filtri esterni sono esplicitamente impostati e diversi dallo stato corrente
    if (Array.isArray(externalFilters) && externalFilters.length > 0) {
      const externalString = JSON.stringify(externalFilters.sort());
      const currentString = JSON.stringify(selectedStatuses.sort());
      
      if (externalString !== currentString) {

        setSelectedStatuses(externalFilters);
        // Aggiorna anche il cookie quando si sincronizza con filtri esterni
        if (showStatusFilter) {
          setCookie(COOKIE_NAME, externalFilters);
        }
      }
    }
  }, [filters, statusFilterField, selectedStatuses, isInitialized, showStatusFilter]);

  // Sincronizzazione dei moduli selezionati con i filtri esterni
  useEffect(() => {
    const externalModules = filters[moduleFilterField];
    if (Array.isArray(externalModules)) {
      if (JSON.stringify(externalModules) !== JSON.stringify(selectedModuleIds)) {
        setSelectedModuleIds(externalModules);
      }
    }
  }, [filters, moduleFilterField, selectedModuleIds]);

  // Caricamento dati iniziali
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoadingProducts(true);
      setLoadingAllModules(true);
      
      try {
        const resources = await generatorApi.fetchResources();
        const productsData = resources.products || [];
        const modulesData = resources.modules || [];
        
        const formattedProducts = (productsData || [])
          .filter(p => p && (p.id || p.PRD_Id_Prodotto))
          .map(p => ({
            id: p.id || p.PRD_Id_Prodotto,
            value: p.id || p.PRD_Id_Prodotto,
            label: `${p.code || p.PRD_CodiceProdotto} - ${p.description || p.PRD_Descrizione}`,
            description: p.description || p.PRD_Descrizione,
            code: p.code || p.PRD_CodiceProdotto,
            displayLabel: `${p.code || p.PRD_CodiceProdotto} - ${p.description || p.PRD_Descrizione}`,
            originalData: p
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        
        const formattedModules = modulesData.map(m => ({
          id: m.id || m.MOD_Id_Moduli,
          value: m.id || m.MOD_Id_Moduli,
          label: m.code || m.MOD_CodiceModulo || m.description || m.MOD_DescrizioneModulo,
          description: m.description || m.MOD_DescrizioneModulo,
          code: m.code || m.MOD_CodiceModulo,
          status: 'active'
        }));
        
        setProducts(formattedProducts);
        setAllModules(formattedModules);
        setAvailableModules(formattedModules);
        
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setProducts([]);
        setAllModules([]);
        setAvailableModules([]);
      } finally {
        setLoadingProducts(false);
        setLoadingAllModules(false);
        setIsInitialized(true);
      }
    };

    fetchInitialData();
  }, []);

  // **CORREZIONE**: Applicazione filtri iniziali migliorata
  useEffect(() => {
    if (!isInitialized || !showStatusFilter || hasExecutedInitialSearch) {
      return;
    }
    
    // Applica sempre i filtri di stato correnti (che possono venire da cookie o default)

    
    // Usa i filtri correnti per la ricerca iniziale
    handleSearchWithFilters(statusFilterField, selectedStatuses);
    setHasExecutedInitialSearch(true);
  }, [isInitialized, showStatusFilter, hasExecutedInitialSearch]);

  // Handler per ricerca con filtri atomica
  const handleSearchWithFilters = useCallback((field, value) => {

    
    // Aggiorna lo stato locale immediatamente
    if (field === statusFilterField) {
      setSelectedStatuses(value);
      // **CORREZIONE**: Salva sempre nei cookie quando cambia lo stato
      if (showStatusFilter && value && value.length > 0) {
        setCookie(COOKIE_NAME, value);
      }
    } else if (field === moduleFilterField) {
      setSelectedModuleIds(value);
    }
    
    // Calcola i nuovi filtri
    const newFilters = { ...filters, [field]: value };
    
    // Aggiorna il filtro nel contesto
    onFilterChange(field, value);
    
    // Esegui la ricerca con i filtri aggiornati
    if (onSearch) {
      setTimeout(() => {

        onSearch(newFilters);
      }, 50);
    }
  }, [onFilterChange, onSearch, statusFilterField, moduleFilterField, showStatusFilter, filters]);

  // Handler per cambio selezione stati
  const handleStatusSelectionChange = useCallback((newSelectedStatuses) => {

    handleSearchWithFilters(statusFilterField, newSelectedStatuses);
  }, [handleSearchWithFilters, statusFilterField]);

  // Handler per doppio click sugli stati
  const handleStatusDoubleClick = useCallback((statusValue) => {
    const exclusiveSelection = [statusValue];

    handleSearchWithFilters(statusFilterField, exclusiveSelection);
  }, [handleSearchWithFilters, statusFilterField]);

  // Handler per reset agli stati default
  const handleResetStatusesToDefault = useCallback(() => {
    const defaultStatuses = ['I', 'C', 'F', 'P', 'M'];

    handleSearchWithFilters(statusFilterField, defaultStatuses);
  }, [handleSearchWithFilters, statusFilterField]);

  // Handler per cambio prodotto
  const handleProductChange = useCallback(async (selectedValue) => {

    setSelectedProductValue(selectedValue);
    
    if (selectedValue) {
      setLoadingProductModules(true);
      try {
        const modulesForProduct = await generatorApi.fetchModules(selectedValue);
        
        const formattedProductModules = modulesForProduct.map(m => ({
          id: m.id || m.MOD_Id_Moduli,
          value: m.id || m.MOD_Id_Moduli,
          label: m.code || m.MOD_CodiceModulo || m.description || m.MOD_DescrizioneModulo,
          description: m.description || m.MOD_DescrizioneModulo,
          code: m.code || m.MOD_CodiceModulo,
          status: 'active'
        }));
        
        setAvailableModules(formattedProductModules);
        
        // Seleziona automaticamente tutti i moduli del prodotto
        const idsToSelect = formattedProductModules.map(m => m.value);
        handleSearchWithFilters(moduleFilterField, idsToSelect);
        
        if (onAdvancedFilterChange) {
          onAdvancedFilterChange({ modules: idsToSelect });
        }
        
      } catch (error) {
        console.error('Error fetching product modules:', error);
        setAvailableModules(allModules);
        handleSearchWithFilters(moduleFilterField, []);
        
        if (onAdvancedFilterChange) {
          onAdvancedFilterChange({ modules: [] });
        }
      } finally {
        setLoadingProductModules(false);
      }
    } else {
      setAvailableModules(allModules);
      handleSearchWithFilters(moduleFilterField, []);
      
      if (onAdvancedFilterChange) {
        onAdvancedFilterChange({ modules: [] });
      }
    }
  }, [allModules, handleSearchWithFilters, moduleFilterField, onAdvancedFilterChange]);

  // Handler per cambio selezione moduli
  const handleModuleSelectionChange = useCallback((newSelectedModuleIds) => {

    handleSearchWithFilters(moduleFilterField, newSelectedModuleIds);
    
    if (onAdvancedFilterChange) {
      onAdvancedFilterChange({ modules: newSelectedModuleIds });
    }
  }, [handleSearchWithFilters, moduleFilterField, onAdvancedFilterChange]);

  // Prodotto selezionato
  const selectedProduct = useMemo(() => {
    if (!selectedProductValue) return null;
    return products.find(p => p.value === selectedProductValue) || null;
  }, [selectedProductValue, products]);

  // Opzioni di stato
  const statusOptions = [
    { value: 'I', label: 'Inserita', color: 'bg-green-50 dark:bg-green-900/30', selectedColor: 'bg-green-600', borderColor: 'border-green-300', textColor: 'text-green-800 dark:text-green-200' },
    { value: 'C', label: 'In lavorazione Cristiana', color: 'bg-pink-50 dark:bg-pink-900/30', selectedColor: 'bg-pink-600', borderColor: 'border-pink-300', textColor: 'text-pink-800 dark:text-pink-200' },
    { value: 'F', label: 'In lavorazione Filippo', color: 'bg-yellow-50 dark:bg-yellow-900/30', selectedColor: 'bg-yellow-600', borderColor: 'border-yellow-300', textColor: 'text-yellow-800 dark:text-yellow-200' },
    { value: 'M', label: 'In lavorazione Marco', color: 'bg-blue-50 dark:bg-blue-900/30', selectedColor: 'bg-blue-600', borderColor: 'border-blue-300', textColor: 'text-blue-800 dark:text-blue-200' },
    { value: 'P', label: 'In lavorazione Paul', color: 'bg-purple-50 dark:bg-purple-900/30', selectedColor: 'bg-purple-600', borderColor: 'border-purple-300', textColor: 'text-purple-800 dark:text-purple-200' },
    { value: 'T', label: 'Terminata', color: 'bg-white dark:bg-gray-800', selectedColor: 'bg-gray-600', borderColor: 'border-gray-300', textColor: 'text-gray-800 dark:text-gray-200' },
    { value: 'D', label: 'Da rilasciare', color: 'bg-cyan-50 dark:bg-cyan-900/30', selectedColor: 'bg-cyan-600', borderColor: 'border-cyan-300', textColor: 'text-cyan-800 dark:text-cyan-200' },
    { value: 'S', label: 'Sospesa', color: 'bg-orange-50 dark:bg-orange-900/30', selectedColor: 'bg-orange-600', borderColor: 'border-orange-300', textColor: 'text-orange-800 dark:text-orange-200' },
    { value: 'U', label: 'Urgente', color: 'bg-red-50 dark:bg-red-900/30', selectedColor: 'bg-red-600', borderColor: 'border-red-300', textColor: 'text-red-800 dark:text-red-200' }
  ];

  // Debug: Log dei valori correnti


  return (
    <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      {/* Header con tema dinamico */}
      <div className="flex items-center space-x-2 mb-4">
        <div className={`p-2 rounded-full bg-${themeColor}-200 dark:bg-${themeColor}-700`}>
          <FaFilter className={`h-4 w-4 text-${themeColor}-700 dark:text-${themeColor}-300`} />
        </div>
        <h3 className={`text-base font-semibold text-${themeColor}-800 dark:text-${themeColor}-100`}>
          Filtri Avanzati
        </h3>
      </div>

      <div className="space-y-6">
        {/* Filtro Prodotto */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            <Tooltip text="Filtra le macro funzioni per prodotto. Selezionando un prodotto verranno mostrati solo i moduli correlati.">
              Prodotto
            </Tooltip>
          </label>
          {products.length > 0 ? (
            <SelectField
              value={selectedProductValue}
              onChange={handleProductChange}
              options={[
                { value: null, label: 'Tutti i prodotti' },
                ...products
              ]}
              placeholder="Seleziona un prodotto..."
              isLoading={loadingProducts}
              className="w-full"
            />
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400 p-3 border border-dashed rounded-lg border-gray-300 dark:border-gray-600">
              {loadingProducts ? 'Caricamento prodotti in corso...' : 'Nessun prodotto disponibile'}
            </div>
          )}
        </div>

        {/* Filtro Moduli */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            <Tooltip text="Seleziona uno o più moduli per filtrare le macro funzioni. I moduli rappresentano le diverse aree funzionali del sistema.">
              Moduli
            </Tooltip>
          </label>
          
          {loadingProductModules && selectedProduct && (
            <div className="text-sm text-blue-600 dark:text-blue-400 p-3 border border-dashed rounded-lg border-blue-300 dark:border-blue-600 mb-3">
              Caricamento moduli per "{selectedProduct.label}"...
            </div>
          )}
          
          {availableModules.length > 0 ? (
            <HorizontalCheckboxGroup
              options={availableModules}
              selectedValues={selectedModuleIds}
              onChange={handleModuleSelectionChange}
              themeColor={themeColor}
              isDark={isDark}
            />
          ) : (
            <div className="text-sm text-gray-500 dark:text-gray-400 p-3 border border-dashed rounded-lg border-gray-300 dark:border-gray-600">
              {loadingAllModules ? 'Caricamento moduli in corso...' : 'Nessun modulo disponibile'}
            </div>
          )}
          
          {selectedProduct && availableModules.length > 0 && (
            <p className="text-xs mt-2 text-gray-500 dark:text-gray-400">
              Mostrando {availableModules.length} moduli per il prodotto "{selectedProduct.label}"
              {selectedModuleIds.length > 0 && ` - ${selectedModuleIds.length} selezionati`}
            </p>
          )}
          
          {!selectedProduct && availableModules.length > 0 && (
            <p className="text-xs mt-2 text-gray-500 dark:text-gray-400">
              Mostrando tutti i {availableModules.length} moduli disponibili
              {selectedModuleIds.length > 0 && ` - ${selectedModuleIds.length} selezionati`}
            </p>
          )}
        </div>

        {/* Filtro Stati */}
        {showStatusFilter && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Filtra per stato (ricerca automatica)
              </h3>
              <button
                onClick={handleResetStatusesToDefault}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline"
              >
                Reset default
              </button>
            </div>
            <HorizontalCheckboxGroup
              options={statusOptions}
              selectedValues={selectedStatuses}
              onChange={handleStatusSelectionChange}
              onDoubleClick={handleStatusDoubleClick}
              showSelectAll={false}
              labelKey="label"
              valueKey="value"
              colorKey="color"
              selectedColorKey="selectedColor"
              borderColorKey="borderColor"
              textColorKey="textColor"
            />
          </div>
        )}

        {/* Riepilogo selezioni */}
        <div className={`text-sm pt-4 border-t border-gray-200 dark:border-gray-700
                        bg-gradient-to-r from-${themeColor}-50 to-white dark:from-${themeColor}-900/20 dark:to-gray-800/50
                        p-3 rounded-lg`}>
          <div className="flex flex-wrap gap-4">
            <span className="flex items-center">
              <strong className={`text-${themeColor}-700 dark:text-${themeColor}-300 mr-1`}>Prodotto:</strong> 
              <span className="text-gray-600 dark:text-gray-300">
                {selectedProduct ? selectedProduct.label : 'Nessuno'}
              </span>
            </span>
            <span className="flex items-center">
              <strong className={`text-${themeColor}-700 dark:text-${themeColor}-300 mr-1`}>Moduli:</strong> 
              <span className="text-gray-600 dark:text-gray-300">
                {selectedModuleIds.length} selezionati
              </span>
            </span>
            {showStatusFilter && (
              <span className="flex items-center">
                <strong className={`text-${themeColor}-700 dark:text-${themeColor}-300 mr-1`}>Stati:</strong> 
                <span className="text-gray-600 dark:text-gray-300">
                  {selectedStatuses.length} selezionati
                  {selectedStatuses.length > 0 && (
                    <span className="ml-1 text-xs">
                      ({selectedStatuses.map(s => statusOptions.find(opt => opt.value === s)?.label).join(', ')})
                    </span>
                  )}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdvancedFilterControls;